import { admin, user, search, order, cases, billing } from "../../api";
import { call, put, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";
import { toastSuccess, toastError } from "@utils/utils";

import { errorHandler } from "../ui/uiSlice";
import {
  SEARCH_PARTNERS,
  GET_ORDERS,
  SEARCH_ORDERS,
  CANCEL_ORDER,
  GET_CASES,
  GET_CASES_BY_USER,
  SEARCH_TRANSACTIONS,
  DELETE_PARTNER,
  GET_PROMO_CODE,
  GET_REDEMPTIONS,
  UPDATE_CASE_SHIPPING,
  UPDATE_CASE_ADDRESS,
  UPDATE_ORDER_SHIPPING_METHOD,
  CREATE_USER_ADDRESS,
  UPDATE_USER_ADDRESS,
  SEND_ADMIN_ORDER,
  SEARCH_USER_BY_EMAIL,
  SEARCH_USER_BY_ID,
  REPLACE_ORDER,
  CANCEL_REDO_ORDER,
  FIND_REDO_ORDER,
  FIND_SHIP_CASE,
  REPLACE_REDO_ORDER,
  RESEND_REDO_ORDER,
  LOAD_REDO_PRESCRIPTIONS,
  LOAD_REDO_INVOICE,
  UPDATE_SHIP_METHOD,
  LOAD_SHIP_ADDRESSES,
  SAVE_SHIP_ADDRESS,
  SAVE_USER_ADDRESS,
  CHANGE_SHIP_ADDRESS,
} from "./adminAction";
import * as adminSlice from "./adminSlice";
import { describeAlreadyReplaced, isReplacementAlreadyDone } from "./redoReplacement";
import { readInvoicePayment, readInvoiceProvider } from "./redoInvoicePayment";
import { selectState } from "@store/selectState";

function* getUserByEmail(action) {
  yield put(adminSlice.setIsSearchingUsers(true));
  yield put(adminSlice.setUserSearchStatus("pending"));
  try {
    const { data } = yield call(() => user.userGet(`search?email=${action.payload.email}`));
    yield put(adminSlice.findUser(data));
    yield put(adminSlice.setUserSearchStatus("found"));
  } catch (error) {
    yield put(errorHandler("Could not get user"));
    // GET user/search returns 404 "User not found" for a genuine no-match —
    // distinct from a real failure, so FIND-E6 can show an explicit message.
    yield put(
      adminSlice.setUserSearchStatus(error?.response?.status === 404 ? "not_found" : "error")
    );
  } finally {
    yield put(adminSlice.setIsSearchingUsers(false));
  }
}

function* getUserById(action) {
  yield put(adminSlice.setIsSearchingUsers(true));
  try {
    const { data } = yield call(() => user.userGet(`search?id=${action.payload.id}`));
    yield put(adminSlice.findUser(data));
  } catch (error) {
    yield put(errorHandler("Could not get user"));
  } finally {
    yield put(adminSlice.setIsSearchingUsers(false));
  }
}

function* createUserAddress(action) {
  try {
    const foundUser = yield selectState((state) => state.admin.users);
    const { data } = yield call(() =>
      user.userPost("address", { address: { ...action.payload, country: "us" } })
    );

    // Append immutably — Array.prototype.push returns the new length (a number),
    // which previously overwrote state.admin.users.addresses and broke the list
    // until a full reload (ENG-482).
    const addresses = [...(foundUser.addresses ?? []), data];
    yield put(adminSlice.findUser({ ...foundUser, addresses }));
    toastSuccess("Address created.");
  } catch (error) {
    console.log(error);
    // errorHandler only sets ui state (not rendered in admin) — use a toast.
    toastError("Could not create the address. Please try again.");
  }
}

function* updateUserAddress(action) {
  try {
    const foundUser = yield selectState((state) => state.admin.users);
    // Admin-on-behalf edit: PUT /user/address updates the existing row by id
    // (backend allows admins to edit any user's address). US-only, no country field.
    const { data } = yield call(() =>
      user.userPut("address", {
        address: { ...action.payload, UserId: foundUser.id, country: "us" },
      })
    );

    // Reflect the edit in place — swap the updated row, no full reload (parity
    // with the CREATE_USER_ADDRESS add flow).
    const addresses = (foundUser.addresses ?? []).map((address) =>
      address.id === data.id ? data : address
    );
    yield put(adminSlice.findUser({ ...foundUser, addresses }));
    toastSuccess("Address updated.");
  } catch (error) {
    console.log(error);
    // errorHandler only sets ui state (not rendered in admin) — use a toast.
    toastError("Could not update the address. Please try again.");
  }
}

function* getOrders(action) {
  yield put(adminSlice.setIsSearchingOrders(true));
  try {
    const { data } = yield call(() => order.orderGet("/search", { params: action.payload }));
    yield put(adminSlice.getOrders(data));
    // Stash the query so mutation sagas can refresh the same search instead of
    // collapsing the table to a single successor row.
    yield put(adminSlice.setLastOrderQuery(action.payload));
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not get order details"));
  } finally {
    yield put(adminSlice.setIsSearchingOrders(false));
  }
}

// A search the admin ran in the rebuilt Order Search: its outcome goes to `orderSearch`
// and the legacy order list is left alone.
function* searchOrders(action) {
  yield put(adminSlice.orderSearchPending());
  try {
    const { data } = yield call(() => order.orderGet("/search", { params: action.payload }));
    yield put(adminSlice.orderSearchFetchSuccess(data));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.orderSearchError());
  }
}

// Re-run the last order search so a mutation's result appears in-context
// (alongside its predecessor/history) rather than collapsing the table.
function* refreshOrders() {
  const lastQuery = yield selectState((state) => state.admin.lastOrderQuery);
  if (lastQuery) {
    yield put({ type: GET_ORDERS, payload: lastQuery });
  }
}

// Loads the legacy `cases` list. Returns the response data, or undefined on failure.
function* loadCases(params) {
  yield put(adminSlice.setIsSearchingCases(true));
  try {
    const { data } = yield call(() => search("case", { params }));
    yield put(adminSlice.getCases(data));
    return data;
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not get order details"));
    return undefined;
  } finally {
    yield put(adminSlice.setIsSearchingCases(false));
  }
}

// A search the admin ran in the v2 Case Search panel: remember its query so
// later refreshes can re-run it, and report the outcome in `caseSearch`.
function* getCases(action) {
  yield put(adminSlice.setLastCaseSearchParams(action.payload));
  yield put(adminSlice.caseSearchPending());
  const data = yield call(loadCases, action.payload);
  if (data) {
    yield put(adminSlice.caseSearchFetchSuccess(data));
    return;
  }
  yield put(adminSlice.caseSearchError());
}

// Refresh after a case mutation: the legacy list reloads as before, and the v2
// Case Search panel re-runs its own last query so its filters and page stay put.
function* refreshCases() {
  yield call(loadCases, {});
  const lastParams = yield selectState((state) => state.admin.lastCaseSearchParams);
  if (!lastParams) return;

  try {
    const { data } = yield call(() => search("case", { params: lastParams }));
    yield put(adminSlice.caseSearchFetchSuccess(data));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.caseSearchError());
  }
}

// The Case search covers only the current month unless it is given a start date, so the Ship
// screen names one that reaches every Case (SHIPUI-R3).
const shipSearchStartDate = "2000-01-01";

// The Case with this Case ID and the Orders on it, newest first (SHIPUI-R3, R9).
function* loadShipCase(caseId) {
  const { data: page } = yield call(() =>
    search("case", { params: { id: caseId, from: shipSearchStartDate, offset: 0 } })
  );
  // Only a Case whose Case ID is the one asked for: a near match is another patient's Case.
  const shipCase = page.rows?.find((row) => row.caseId === caseId);
  if (!shipCase) return null;

  const { data: found } = yield call(() =>
    order.orderGet("/search", { params: { orderId: caseId, offset: 0 } })
  );
  const orders = found
    .filter((foundOrder) => foundOrder.Case?.id === shipCase.id || foundOrder.caseId === caseId)
    .sort((first, second) => second.id - first.id);

  return { shipCase, orders };
}

function* findShipCase(action) {
  yield put(adminSlice.shipLookupPending());
  try {
    const loaded = yield call(loadShipCase, action.payload.caseId);
    if (!loaded) {
      yield put(adminSlice.shipLookupNotFound());
      return;
    }
    yield put(adminSlice.shipLookupSuccess(loaded));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.shipLookupError());
  }
}

// The Order whose Order ID, or whose Case ID, is exactly the text asked for, then its Case with
// every Order on it (REDOUI-R3, R4). A text that only starts like an ID finds nothing.
function* findRedoOrder(action) {
  yield put(adminSlice.redoLookupPending());
  try {
    const query = action.payload.query;
    const { data: found } = yield call(() =>
      order.orderGet("/search", { params: { orderId: query, offset: 0 } })
    );
    const match =
      found.find((foundOrder) => foundOrder.order_id === query) ??
      found.find((foundOrder) => foundOrder.caseId === query);
    // An Order with no Case ID leads to no Case: searching without one could show another patient's.
    if (!match?.caseId) {
      yield put(adminSlice.redoLookupNotFound());
      return;
    }

    const loaded = yield call(loadShipCase, match.caseId);
    if (!loaded) {
      yield put(adminSlice.redoLookupNotFound());
      return;
    }

    const isOrderIdAsked = match.order_id === query;
    const newestOrderId = loaded.orders[0]?.id ?? match.id;
    const selectedOrderId =
      isOrderIdAsked && loaded.orders.some((foundOrder) => foundOrder.id === match.id)
        ? match.id
        : newestOrderId;
    yield put(adminSlice.redoLookupSuccess({ ...loaded, selectedOrderId }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.redoLookupError());
  }
}

// The pharmacy is asked to stop an Order only while it is new or in progress (REDOUI-R22).
const cancelledAtPharmacyStatuses = ["new", "in_progress"];

// Cancels the Order the Redo screen shows (REDOUI-R24…R27): the Backend decides what the pharmacy
// is asked; a failure keeps the Order as it was and carries the server's reason.
function* cancelRedoOrder(action) {
  const { id, orderId, status } = action.payload;
  yield put(adminSlice.redoCancelPending());
  try {
    yield call(() => order.orderPut(`/${id}/cancel`));
    yield put(
      adminSlice.redoCancelSucceeded({
        id,
        orderId,
        wasAtPharmacy: cancelledAtPharmacyStatuses.includes(status),
      })
    );
  } catch (error) {
    console.log(error);
    yield put(
      adminSlice.redoCancelFailed({
        orderId,
        orderStatus: status ?? "not yet sent",
        reason: error?.response?.data?.message ?? error?.message ?? "Unknown error",
      })
    );
  }
}

const describeServerFailure = (error) =>
  error?.response?.data?.message ?? error?.message ?? "Unknown error";

// The Orders of the Case read again, the Order given chosen (REDOUI-R36, R38). A failed reading
// leaves the list as it was: the outcome of the replacement is still told.
function* refreshRedoOrders(caseId, chosenOrder) {
  try {
    const loaded = yield call(loadShipCase, caseId);
    if (loaded) {
      const chosenOrderId =
        loaded.orders.find((found) => found.order_id === chosenOrder.order_id)?.id ??
        loaded.orders[0]?.id;
      yield put(adminSlice.redoOrdersRefreshed({ ...loaded, selectedOrderId: chosenOrderId }));
    }
  } catch (error) {
    console.log(error);
  }
}

// Sends the new Order to the pharmacy and tells how it went (REDOUI-R35, R38).
function* sendRedoSuccessor({ successor, prescriptions, invoiceId, caseId }) {
  const outcome = { successorOrderId: successor.order_id, invoiceId, caseId };
  let failure = null;

  try {
    yield call(() => order.orderPut(`/${successor.order_id}/send`, { prescriptions }));
  } catch (error) {
    console.log(error);
    failure = describeServerFailure(error);
  }

  yield call(refreshRedoOrders, caseId, successor);
  yield put(
    failure === null
      ? adminSlice.redoReplaceSucceeded(outcome)
      : adminSlice.redoReplaceNotSent({ ...outcome, reason: failure, prescriptions })
  );
}

// Replaces the Order the Redo screen shows (REDOUI-R35…R38): the reissue first, the send of the
// new Order only once it answered. No legacy delete-and-create sequence.
function* replaceRedoOrder(action) {
  const { id, orderId, status, invoiceId, caseId, prescriptions } = action.payload;
  yield put(adminSlice.redoReplacePending());
  let successor;
  try {
    const { data } = yield call(() => order.orderPost(`/${id}/reissue`));
    successor = data;
  } catch (error) {
    console.log(error);
    yield put(
      adminSlice.redoReplaceFailed({
        orderId,
        orderStatus: status ?? "not yet sent",
        reason: describeServerFailure(error),
      })
    );
    return;
  }

  const { orders } = yield selectState((state) => state.admin.redoScreen);
  const replacedOrder = orders.find((shown) => shown.id === id);
  if (isReplacementAlreadyDone({ replacedOrder, answer: successor })) {
    yield call(refreshRedoOrders, caseId, successor);
    yield put(
      adminSlice.redoReplaceAlreadyDone(
        describeAlreadyReplaced({
          orderId,
          answer: successor,
          prescriptions,
          invoiceId,
          caseId,
        })
      )
    );
    return;
  }

  yield call(sendRedoSuccessor, { successor, prescriptions, invoiceId, caseId });
}

// Sends the new Order again after its send failed: no reissue (REDOUI-R39).
function* resendRedoOrder(action) {
  const { orderId, prescriptions, invoiceId, caseId } = action.payload;
  yield put(adminSlice.redoReplaceResending());
  yield call(sendRedoSuccessor, {
    successor: { order_id: orderId, status: null },
    prescriptions,
    invoiceId,
    caseId,
  });
}

// The prescriptions of the Order the Redo screen shows (REDOUI-R18).
function* loadRedoPrescriptions(action) {
  const { orderId } = action.payload;
  yield put(adminSlice.redoPrescriptionsPending(orderId));
  try {
    const { data } = yield call(() => order.orderGet(`/${orderId}/prescriptions`));
    yield put(adminSlice.redoPrescriptionsLoaded({ orderId, items: data }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.redoPrescriptionsFailed(orderId));
  }
}

// The payment state of the invoice that funds the Order the Redo screen shows (REDOUI-R77, R81).
function* loadRedoInvoice(action) {
  const { orderId } = action.payload;
  yield put(adminSlice.redoInvoicePending(orderId));
  try {
    const { data } = yield call(() => order.orderGet(`/${orderId}/invoice`));
    yield put(
      adminSlice.redoInvoiceSettled({
        orderId,
        payment: readInvoicePayment(data),
        provider: readInvoiceProvider(data),
      })
    );
  } catch (error) {
    console.log(error);
    yield put(adminSlice.redoInvoiceSettled({ orderId, payment: "failed" }));
  }
}

const describeFailure = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message ?? error?.message ?? "Unknown error";

  return status ? `${status} "${message}"` : message;
};

// A shipping-method change (SHIPUI-R16): the Case's method, then the active Order's replacement,
// then the new Order's method and its dispatch, each only after the step before succeeded
// (SHIP-R1, ENG-770). The Case's previous method is set back when the replacement fails.
function* updateShipMethod(action) {
  const { caseNumber, shippingMethod, activeOrder } = action.payload;
  const { shipCase } = yield selectState((state) => state.admin.shipScreen);
  const previousMethod = shipCase?.shipping_method ?? "standard";
  yield put(adminSlice.shipUpdatePending());

  try {
    yield call(() => cases.casePut(`/${caseNumber}`, { shipping_method: shippingMethod }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.shipUpdateFailed({ reason: describeFailure(error) }));
    return;
  }

  if (!activeOrder) {
    yield put(adminSlice.shipUpdateCaseOnly({ method: shippingMethod }));
    yield call(refreshShipCase, shipCase.caseId);
    return;
  }

  let successor;
  try {
    const { data } = yield call(() => order.orderPost(`/${activeOrder.id}/reissue`));
    successor = data;
  } catch (error) {
    console.log(error);
    try {
      yield call(() => cases.casePut(`/${caseNumber}`, { shipping_method: previousMethod }));
    } catch (rollbackError) {
      console.log(rollbackError);
    }
    yield put(adminSlice.shipUpdateFailed({ reason: describeFailure(error) }));
    return;
  }

  try {
    yield call(() => order.orderPut(`/${successor.order_id}`, { shipping_method: shippingMethod }));
    yield call(() => order.orderPut(`/${successor.order_id}/send`, {}));
  } catch (error) {
    console.log(error);
    yield call(refreshShipCase, shipCase.caseId);
    yield put(
      adminSlice.shipUpdateReplacedNotSent({
        predecessorOrderId: activeOrder.order_id,
        successorOrderId: successor.order_id,
        reason: describeFailure(error),
      })
    );
    return;
  }

  yield call(refreshShipCase, shipCase.caseId);
  yield put(
    adminSlice.shipUpdateReplaced({
      method: shippingMethod,
      predecessorOrderId: activeOrder.order_id,
      successorOrderId: successor.order_id,
    })
  );
}

// Re-reads the Case and its Orders so the context and the card show what changed (SHIPUI-R18).
function* refreshShipCase(caseId) {
  try {
    const loaded = yield call(loadShipCase, caseId);
    if (loaded) yield put(adminSlice.shipCaseRefreshed(loaded));
  } catch (error) {
    console.log(error);
  }
}

const toShipAddress = (address) => ({
  id: String(address.id),
  street: address.street,
  secondary: address.secondary || undefined,
  city: address.city,
  state: address.state,
  postalCode: address.postalCode,
});

// The addresses the admin can choose among for a Case: the User's saved ones (SHIPUI-R23).
function* loadShipAddresses(action) {
  yield put(adminSlice.shipAddressDialogOpened());
  try {
    const { data } = yield call(() => user.userGet(`${action.payload.userId}/addresses`));
    yield put(adminSlice.shipAddressesLoaded({ addresses: data.map(toShipAddress) }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.shipAddressesFailed());
  }
}

// Creates an address on the User, or fixes one in place when it has an id (SHIPUI-R30).
function* saveShipAddress(action) {
  const { userId, fields } = action.payload;
  const { id, ...rest } = fields;
  yield put(adminSlice.shipAddressFailureCleared());
  try {
    const { data } = yield call(() =>
      id
        ? user.userPut("address", { address: { ...rest, id, UserId: userId, country: "us" } })
        : user.userPost("address", { address: { ...rest, UserId: userId, country: "us" } })
    );
    yield put(adminSlice.shipAddressStored({ address: toShipAddress(data) }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.shipAddressFailed({ reason: describeFailure(error) }));
  }
}

// Saves an address from the User Search (USER-R25…R28): created on the found user, or updated in
// place when it has an id, then listed on that user without a reload.
function* saveUserAddress(action) {
  const { userId, fields } = action.payload;
  const { id, ...rest } = fields;
  yield put(adminSlice.userAddressSavePending({ userId }));
  try {
    const { data } = yield call(() =>
      id
        ? user.userPut("address", { address: { ...rest, id, UserId: userId, country: "us" } })
        : user.userPost("address", { address: { ...rest, UserId: userId, country: "us" } })
    );
    yield put(adminSlice.userAddressSaved({ userId, address: data }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.userAddressSaveFailed({ userId, reason: describeFailure(error) }));
  }
}

// An address change (SHIPUI-R24): the address persists on the Case first, and only then is the
// active Order re-sent with it, unless the Order has shipped (SHIP-R2, SHIP-R3).
function* changeShipAddress(action) {
  const { caseNumber, address, activeOrder, hasShipped } = action.payload;
  const { shipCase } = yield selectState((state) => state.admin.shipScreen);
  yield put(adminSlice.shipAddressStep({ step: "saving" }));

  try {
    yield call(() => cases.casePut(`/${caseNumber}`, { shippingAddress: { id: address.id } }));
  } catch (error) {
    console.log(error);
    yield put(adminSlice.shipAddressFailed({ reason: describeFailure(error) }));
    return;
  }

  const finish = function* (outcome) {
    yield call(refreshShipCase, shipCase.caseId);
    yield put(adminSlice.shipAddressChanged({ outcome }));
  };

  if (!activeOrder) {
    yield call(finish, { status: "changed", address, orderId: null });
    return;
  }
  if (hasShipped) {
    yield call(finish, { status: "shipped", address, orderId: activeOrder.order_id });
    return;
  }

  yield put(adminSlice.shipAddressStep({ step: "resending" }));
  try {
    const { data: prescriptions } = yield call(() =>
      order.orderGet(`/${activeOrder.id}/prescriptions`)
    );
    yield call(() => order.orderPut(`/${activeOrder.order_id}/send`, { prescriptions }));
  } catch (error) {
    console.log(error);
    yield call(finish, {
      status: "notResent",
      address,
      orderId: activeOrder.order_id,
      reason: describeFailure(error),
    });
    return;
  }

  yield call(finish, { status: "changed", address, orderId: activeOrder.order_id });
}

function* getPromoCode(action) {
  yield put(adminSlice.setIsSearchingPromoCodes(true));
  try {
    const { data } = yield call(() =>
      billing.billingGet("/promotions", { params: action.payload })
    );
    yield put(adminSlice.getPromoCode(data));
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not get promo code"));
  } finally {
    yield put(adminSlice.setIsSearchingPromoCodes(false));
  }
}

function* getRedemptions(action) {
  try {
    const { data } = yield call(() =>
      billing.billingGet(`/promotions/${action.payload.promoCodeId}/redemptions`)
    );
    yield put(adminSlice.getRedemptions(data));
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not get redemptions of this promo code"));
  }
}

function* getCasesByUser(action) {
  yield put(adminSlice.userCasesPending());
  yield put(adminSlice.setIsSearchingCases(true));
  try {
    const { data } = yield call(() => user.userGet(`${action.payload}/cases`));
    // `cases` stays filled for the legacy admin; the rebuilt admin reads `userCases` (CVIEW-R13).
    yield put(adminSlice.getCases({ rows: data }));
    yield put(adminSlice.userCasesFetchSuccess(data));
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not get order details"));
  } finally {
    yield put(adminSlice.setIsSearchingCases(false));
  }
}

function* searchTransactions(action) {
  try {
    const result = yield call(() =>
      admin.partnerGet(`/transactions/search?partnerKey=${action.payload}`)
    );
    yield put(adminSlice.getTransactions(result.data));
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not get transactions."));
  }
}

function* searchPartners() {
  try {
    const result = yield call(() => admin.partnerGet("s/search"));
    yield put(adminSlice.getPartners(result.data));
  } catch (error) {
    console.log(error);
    yield put(errorHandler("Could not search partners"));
  }
}

function* deletePartner(action) {
  yield put(adminSlice.setDeletingPartnerId(action.payload));
  try {
    yield call(() => admin.partnerDelete(`/${action.payload}`));
    yield put(adminSlice.removePartner(action.payload));
  } catch (error) {
    console.log(error);
    toastError("Could not delete partner.");
    yield put(errorHandler("Could not delete partner"));
  } finally {
    yield put(adminSlice.setDeletingPartnerId(null));
  }
}

function* cancelOrder(action) {
  const { orderId, referenceId } = action.payload;
  yield put(adminSlice.cancelOrderError(false));
  yield put(adminSlice.setCancellingOrderId(orderId.toString()));
  try {
    // The backend flips the status synchronously and returns the updated
    // order. Patch the row in place first so it reads `cancelled` right away
    // (spec REDO-E1/R8), then re-run the search for the rest of the table.
    const { data } = yield call(() => order.orderPut(`/${orderId}/cancel`));
    yield put(adminSlice.updateOrder(data));
    toastSuccess("Order cancelled", `OID ${referenceId ?? orderId} is now cancelled.`);
    yield call(refreshOrders);
  } catch (error) {
    console.log(error);
    yield put(adminSlice.cancelOrderError(true));
    // Surface the backend's reason (e.g. "already shipped", spec REDO-E2)
    // instead of a generic dead-end the admin learns to ignore.
    toastError(
      "Could not cancel order",
      error?.response?.data?.message ?? `OID ${referenceId ?? orderId} was not cancelled.`
    );
  } finally {
    yield put(adminSlice.setCancellingOrderId(null));
  }
}

function* updateCaseShipping(action) {
  const orderId = action.payload?.orderId;
  yield put(adminSlice.caseShippingError(false));
  yield put(adminSlice.setIsCaseShippingUpdating(true));
  // Own the shared cross-entry-point flag for the whole duration of this saga
  // (including the trailing getCases refresh below) when the case has an
  // order — updateOrderShippingMethod is told to skip its own tracking via
  // skipInFlightTracking so its `finally` doesn't clear the flag early and
  // cause a flicker before this saga is actually done.
  if (orderId) {
    yield put(
      adminSlice.setOrderActionInFlight({ orderId: orderId.toString(), inFlight: true })
    );
  }
  try {
    yield call(() =>
      cases.casePut(`/${action.payload.caseId}`, {
        shipping_method: action.payload.shipping_method,
      })
    );

    if (orderId) {
      yield call(updateOrderShippingMethod, {
        payload: {
          orderId,
          shippingMethod: action.payload.shipping_method,
          skipInFlightTracking: true,
        },
      });
    }

    yield call(refreshCases);
  } catch (error) {
    console.log(error);
    yield put(adminSlice.caseShippingError(true));
    yield put(errorHandler("Could not update case shipping"));
  } finally {
    yield put(adminSlice.setIsCaseShippingUpdating(false));
    if (orderId) {
      yield put(
        adminSlice.setOrderActionInFlight({ orderId: orderId.toString(), inFlight: false })
      );
    }
  }
}

function* updateCaseAddress(action) {
  const hasOrder = Boolean(action.payload.orderId && action.payload.referenceId);
  yield put(adminSlice.caseShippingError(false));
  yield put(adminSlice.setIsUpdatingAddress(true));

  try {
    // 1) Persist the reassigned address and AWAIT it.
    try {
      yield call(() =>
        cases.casePut(`/${action.payload.caseId}`, {
          shippingAddress: { id: action.payload.addressId },
        })
      );
    } catch (error) {
      console.log(error);
      yield put(adminSlice.caseShippingError(true));
      // errorHandler only sets ui state (not rendered in admin) — use a toast.
      toastError("Could not update the shipping address. Please try again.");
      return;
    }

    // 2) Re-send the order (if any) ONLY after the address persisted, so the
    //    pharmacy receives the NEW address — this replaces the old setTimeout
    //    race (ENG-472); the /send re-reads the case's persisted address.
    if (hasOrder) {
      try {
        const { data: prescriptions } = yield call(() =>
          order.orderGet(`/${action.payload.orderId}/prescriptions`)
        );
        yield call(() =>
          order.orderPut(`/${action.payload.referenceId}/send`, { prescriptions })
        );
      } catch (error) {
        console.log(error);
        // The address DID persist — be explicit so the admin isn't misled.
        toastError("Address updated, but re-sending the order to the pharmacy failed.");
        yield call(refreshCases);
        return;
      }
    }

    toastSuccess(
      hasOrder ? "Shipping address updated and order re-sent." : "Shipping address updated."
    );
    yield call(refreshCases);
  } finally {
    yield put(adminSlice.setIsUpdatingAddress(false));
  }
}

function* sendAdminOrder(action) {
  try {
    yield call(() =>
      order.orderPut(`/${action.payload?.referenceId}/send`, {
        prescriptions: action.payload.prescriptions,
      })
    );
    toastSuccess("Order has been re-sent.");
  } catch (err) {
    toastError("Could not send order.");
  }
}

function* updateOrderShippingMethod(action) {
  const orderId = action.payload?.orderId;
  const skipInFlightTracking = Boolean(action.payload?.skipInFlightTracking);
  yield put(adminSlice.orderShippingError(false));
  // Shared cross-entry-point lock (ENG-770): OrderSearch's row select,
  // OrderManager's Replace Order modal, and CaseResults' case-level select can
  // each independently trigger a reissue for the same order. Skipped when
  // updateCaseShipping calls this saga internally — it owns the flag itself
  // for its full duration (including its trailing getCases refresh) to avoid
  // a flicker where this saga's own finally would clear it too early.
  if (!skipInFlightTracking) {
    yield put(adminSlice.setOrderActionInFlight({ orderId: orderId?.toString(), inFlight: true }));
  }

  // A method change takes effect through a replacement (spec SHIP-R1, ENG-485):
  // the pharmacy does not reliably honor in-place changes to an existing order.
  // The predecessor is never written to — updating it would trigger the
  // status-reset side effect (stamps `new`), which on a shipped order would
  // falsify its lifecycle. Instead: reissue, set the new method on the
  // successor, dispatch the successor.
  let successor;
  try {
    try {
      const { data } = yield call(() => order.orderPost(`/${orderId}/reissue`));
      successor = data;
    } catch (error) {
      console.log(error);
      yield put(adminSlice.orderShippingError(true));
      // Surface the backend's reason instead of a generic dead-end.
      toastError(error?.response?.data?.message ?? "Could not update the shipping method.");
      return;
    }

    try {
      yield call(() =>
        order.orderPut(`/${successor.order_id}`, {
          shipping_method: action.payload?.shippingMethod,
        })
      );
      yield call(() => order.orderPut(`/${successor.order_id}/send`, {}));
    } catch (error) {
      console.log(error);
      yield put(adminSlice.orderShippingError(true));
      // The replacement DID happen — refresh so the table reflects reality, and
      // point the admin at the successor so they finish (set method / re-send)
      // rather than replace again.
      yield call(refreshOrders);
      toastError(
        `Order replaced (new OID: ${successor.order_id}), but setting the method or dispatching failed. Finish it from the order actions.`
      );
      return;
    }

    yield call(refreshOrders);
    toastSuccess(
      `Shipping method updated — order replaced and sent. New OID: ${successor.order_id}`
    );
  } finally {
    if (!skipInFlightTracking) {
      yield put(
        adminSlice.setOrderActionInFlight({ orderId: orderId?.toString(), inFlight: false })
      );
    }
  }
}

function* replaceOrder(action) {
  const orderId = action.payload?.orderId;
  yield put(adminSlice.setOrderActionInFlight({ orderId: orderId?.toString(), inFlight: true }));
  try {
    // 1) Supersede the order server-side (ENG-479). One atomic, admin-only,
    //    per-order-locked call: cancel-if-possible (a shipped order is kept and
    //    reshipped additively), mint the successor, inherit the original PAID
    //    invoice (no re-charge). No hard delete — the old order stays as history.
    let successor;
    try {
      const { data } = yield call(() => order.orderPost(`/${orderId}/reissue`));
      successor = data;
    } catch (error) {
      console.log(error);
      // Surface the backend's reason (e.g. the pharmacy declined the cancel)
      // instead of a generic dead-end.
      toastError(error?.response?.data?.message ?? "Could not replace order.");
      return;
    }

    // 2) Dispatch the successor with the admin's chosen prescriptions.
    try {
      yield call(() =>
        order.orderPut(`/${successor.order_id}/send`, {
          prescriptions: action.payload.prescriptions,
        })
      );
    } catch (error) {
      console.log(error);
      // The replacement DID happen — the successor exists and holds the invoice.
      // Refresh the search so the table reflects reality (successor alongside its
      // predecessor/history), and be explicit so the admin retries the send, not
      // the whole replacement.
      yield call(refreshOrders);
      toastError(
        `Order replaced (new OID: ${successor.order_id}), but sending it to the pharmacy failed. Re-send it from the order actions.`
      );
      return;
    }

    yield call(refreshOrders);
    toastSuccess(`Order replaced and sent to the pharmacy. New OID: ${successor.order_id}`);
  } finally {
    yield put(adminSlice.setOrderActionInFlight({ orderId: orderId?.toString(), inFlight: false }));
  }
}

export default function* adminSaga() {
  yield takeEvery(SEARCH_USER_BY_EMAIL, getUserByEmail);
  yield takeEvery(SEARCH_USER_BY_ID, getUserById);
  yield takeEvery(CREATE_USER_ADDRESS, createUserAddress);
  yield takeEvery(UPDATE_USER_ADDRESS, updateUserAddress);
  yield takeEvery(GET_ORDERS, getOrders);
  yield takeEvery(SEARCH_ORDERS, searchOrders);
  yield takeLatest(FIND_SHIP_CASE, findShipCase);
  yield takeLatest(FIND_REDO_ORDER, findRedoOrder);
  yield takeLeading(CANCEL_REDO_ORDER, cancelRedoOrder);
  yield takeLeading(REPLACE_REDO_ORDER, replaceRedoOrder);
  yield takeLeading(RESEND_REDO_ORDER, resendRedoOrder);
  yield takeLatest(LOAD_REDO_PRESCRIPTIONS, loadRedoPrescriptions);
  yield takeLatest(LOAD_REDO_INVOICE, loadRedoInvoice);
  // takeLeading: a change replaces the Order, so a second dispatch while one runs would mint a duplicate.
  yield takeLeading(UPDATE_SHIP_METHOD, updateShipMethod);
  yield takeLatest(LOAD_SHIP_ADDRESSES, loadShipAddresses);
  yield takeLeading(SAVE_SHIP_ADDRESS, saveShipAddress);
  // takeEvery: a save for another user must not be dropped; the busy "Save address" Button keeps
  // one user's form from saving twice (USER-R26).
  yield takeEvery(SAVE_USER_ADDRESS, saveUserAddress);
  // takeLeading: a change re-sends the Order, so a second dispatch while one runs would send it twice.
  yield takeLeading(CHANGE_SHIP_ADDRESS, changeShipAddress);
  yield takeEvery(GET_CASES, getCases);
  yield takeEvery(GET_CASES_BY_USER, getCasesByUser);
  yield takeLeading(CANCEL_ORDER, cancelOrder);
  yield takeEvery(SEARCH_PARTNERS, searchPartners);
  yield takeEvery(SEARCH_TRANSACTIONS, searchTransactions);
  yield takeLeading(DELETE_PARTNER, deletePartner);
  yield takeEvery(GET_PROMO_CODE, getPromoCode);
  yield takeEvery(GET_REDEMPTIONS, getRedemptions);
  // takeLeading on both shipping flows: they now reissue (mint a successor),
  // so a re-dispatch while one is in flight would create a duplicate.
  yield takeLeading(UPDATE_CASE_SHIPPING, updateCaseShipping);
  yield takeLeading(UPDATE_CASE_ADDRESS, updateCaseAddress);
  yield takeLeading(UPDATE_ORDER_SHIPPING_METHOD, updateOrderShippingMethod);
  yield takeLeading(SEND_ADMIN_ORDER, sendAdminOrder);
  // takeLeading: a reissue mints a successor; letting a re-dispatch run while
  // one flow is in flight would create a duplicate successor.
  yield takeLeading(REPLACE_ORDER, replaceOrder);
}
